import { useEffect, useState } from 'react';
import { Box } from '@chakra-ui/react';
import axios from 'axios';
import { useAtom } from 'jotai';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import BasePageWithTopNav from 'components/layout/TopNavigationLayout';
import PageMetadata from 'components/PageMetadata';
import ScoutAgentMainView from 'components/widgets/ScoutAgent/MainView';
import ScoutAgentSidebar from 'components/widgets/ScoutAgent/Sidebar';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import {
  useAllScoutAgentChats,
  useCreateScoutAgentChat,
  useScoutAgentChatById,
  SCOUT_CHAT_BY_ID_QUERY_KEY,
} from 'hooks/useScoutAgent';
import { selectedScoutChatIdAtom } from 'state/scoutChat';
import { trackPageView, trackScoutAgentInteraction } from 'utils/analytics';
import { ScountAgentChatViewSections } from 'utils/analytics/constants';
import { QUERY_PARAM } from 'utils/constants/QueryParameters';
import { FLAGS } from 'utils/featureFlags';
import type { Session, ScoutChatItem, SessionEvent } from 'services/API/AIInteractionAPI';

function buildOptimisticSession(message: string, tempId: string): Session {
  const now = new Date().toISOString();
  const userEvent: SessionEvent = {
    text: [message],
    author: 'user',
    role: 'user',
    timestamp: now,
  };

  return {
    session_id: tempId,
    user_id: 0,
    display_name: message.slice(0, 50) || 'New chat',
    created_at: now,
    updated_at: now,
    events: [userEvent],
  };
}

function buildOptimisticChatItem(message: string, tempId: string): ScoutChatItem {
  const now = new Date().toISOString();

  return {
    session_id: tempId,
    display_name: message.slice(0, 50) || 'New chat',
    user_id: 0,
    created_at: now,
    updated_at: now,
  };
}

const layoutStyle = {
  display: 'flex',
  flexDirection: 'row',
  height: '100%',
  width: '100%',
};

const FALLBACK_NO_CONTENT_MESSAGE =
  "I'm sorry, I couldn't figure out your request on my end. Could you try again?";

const TIMEOUT_ERROR_MESSAGE =
  "Something's taking longer than usual. Refresh to try again.";

const ERROR_MESSAGE = 'Something went wrong. Refresh your page, and try again.';

const NEW_CHAT_ID = 'new';

const ScoutAgent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedChatId, setSelectedChatId] = useAtom(selectedScoutChatIdAtom);
  const [optimisticSession, setOptimisticSession] = useState<Session | null>(null);
  const [optimisticChatItem, setOptimisticChatItem] = useState<ScoutChatItem | null>(
    null,
  );

  const queryClient = useQueryClient();
  const { isFlagEnabled } = useFeatureFlags();
  const { data: chats = [] } = useAllScoutAgentChats();

  const chatIdFromUrl = searchParams.get(QUERY_PARAM.scoutAgentChatId);

  useEffect(() => {
    trackPageView({
      sectionName: ScountAgentChatViewSections.SCOUT_AGENT_PAGE,
    });
  }, []);

  useEffect(() => {
    // Set selected chat from URL on initial load
    if (!chatIdFromUrl) {
      return;
    }
    setSelectedChatId(chatIdFromUrl);
  }, [setSelectedChatId]);

  const { data: selectedChat, isLoading: isLoadingSelectedChat } = useScoutAgentChatById(
    selectedChatId ?? null,
  );
  const createChat = useCreateScoutAgentChat();

  const isShowingOptimistic =
    optimisticSession != null && selectedChatId === optimisticSession.session_id;
  const handleNewChat = () => {
    setSelectedChatId(null);
    setOptimisticSession(null);
    setOptimisticChatItem(null);
    const next = new URLSearchParams(searchParams);
    next.delete(QUERY_PARAM.scoutAgentChatId);
    setSearchParams(next, { replace: true });
    trackScoutAgentInteraction({
      clickText: 'New chat',
      clickAction: 'create_new_chat',
    });
  };

  const handleSelectChat = (id: string) => {
    trackPageView({
      sectionName: ScountAgentChatViewSections.SCOUT_AGENT_PAGE,
      subSectionName: String(id),
    });

    setSelectedChatId(id);
    const next = new URLSearchParams(searchParams);
    next.set(QUERY_PARAM.scoutAgentChatId, id);
    setSearchParams(next, { replace: true });
  };

  const onClickSidebarItem = (id: string, chat_name: string) => {
    trackScoutAgentInteraction({
      clickText: chat_name,
      clickAction: 'select_previous_chat',
      scoutAgentChatId: id,
    });
    handleSelectChat(id);
  };

  const handlePromptSubmit = (promptValue: string) => {
    const message = promptValue.trim();
    if (!message || createChat.isLoading) return;

    const tempId = `${NEW_CHAT_ID}-${Date.now()}`;
    setSelectedChatId(tempId);
    setOptimisticSession(buildOptimisticSession(message, tempId));
    setOptimisticChatItem(buildOptimisticChatItem(message, tempId));

    trackScoutAgentInteraction({
      clickText: 'Send message',
      clickAction: 'create_new_chat',
      scoutAgentChatId: undefined,
    });

    createChat.mutate(message, {
      onSuccess: (data) => {
        const realSessionId = data.session?.session_id;
        if (!realSessionId) return;

        const userEvent: SessionEvent = {
          text: [message],
          author: 'user',
          role: 'user',
          timestamp: new Date().toISOString(),
        };
        const agentEvent: SessionEvent =
          data?.agent_text?.length > 0
            ? {
                text: data.agent_text,
                author: 'mercator_knowledge_agent',
                role: 'assistant',
                timestamp: new Date().toISOString(),
              }
            : {
                text: [FALLBACK_NO_CONTENT_MESSAGE],
                author: 'mercator_knowledge_agent',
                role: 'assistant',
                timestamp: new Date().toISOString(),
              };
        const now = new Date().toISOString();
        const sessionForCache: Session = {
          session_id: realSessionId,
          user_id: data.session?.user_id ?? 0,
          display_name:
            data.session?.display_name ?? (message.slice(0, 50) || 'New chat'),
          created_at: now,
          updated_at: now,
          events: [userEvent, agentEvent],
        };
        queryClient.setQueryData(
          SCOUT_CHAT_BY_ID_QUERY_KEY(realSessionId),
          sessionForCache,
        );
        setOptimisticSession(null);
        setOptimisticChatItem(null);
        handleSelectChat(realSessionId);
      },
      onError: (error) => {
        const is504 =
          (axios.isAxiosError(error) && error.response?.status === 504) ||
          (error instanceof Error && error.message === '504');
        const errorText = is504 ? TIMEOUT_ERROR_MESSAGE : ERROR_MESSAGE;
        const userEvent: SessionEvent = {
          text: [message],
          author: 'user',
          role: 'user',
          timestamp: new Date().toISOString(),
        };
        const agentEvent: SessionEvent = {
          text: [errorText],
          author: 'mercator_knowledge_agent',
          role: 'assistant',
          timestamp: `error-${Date.now()}`,
        };
        const errorSession: Session = {
          ...buildOptimisticSession(message, tempId),
          events: [userEvent, agentEvent],
        };
        setOptimisticSession(errorSession);
      },
    });
  };

  if (!isFlagEnabled(FLAGS.SCOUT_AGENT)) {
    return null;
  }

  const sidebarChats = optimisticChatItem ? [optimisticChatItem, ...chats] : chats;

  const chatToShow: Session | undefined = isShowingOptimistic
    ? optimisticSession
    : (selectedChat ?? undefined);

  return (
    <BasePageWithTopNav>
      <PageMetadata title="Ask Scout" />
      <Box sx={layoutStyle}>
        <ScoutAgentSidebar
          chats={sidebarChats}
          selectedChatId={selectedChatId}
          onNewChat={handleNewChat}
          onSelectChat={onClickSidebarItem}
        />
        <ScoutAgentMainView
          onPromptSubmit={handlePromptSubmit}
          selectedChatId={selectedChatId}
          selectedChat={chatToShow}
          isLoadingSelectedChat={isShowingOptimistic ? false : isLoadingSelectedChat}
          isAwaitingResponse={createChat.isLoading}
        />
      </Box>
    </BasePageWithTopNav>
  );
};

export default ScoutAgent;
